import React, { useEffect, useMemo, useRef, useState } from "react";
import { CalendarClock, ChevronDown, ChevronUp, MoreHorizontal } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectUserProfile } from "../../features/authentication/authenticationSlice";
import { Session } from "../../features/sessions/models/Session";
import { SessionOccurrence } from "../../features/sessions/models/SessionOccurrence";
import { OccurrenceStatus } from "../../features/sessions/models/OccurrenceStatus";
import {
  selectSessionOccurrences,
  selectOccurrencesLoading,
} from "../../features/sessions/occurrences-slice";
import {
  cancelOccurrence,
  fetchSessionOccurrences,
  rescheduleOccurrence,
  restoreOccurrence,
  retimeOccurrence,
} from "../../features/sessions/slice-extensions/occurrences-slice-extension";
import { fetchSessionById } from "../../features/sessions/slice-extensions/sessions-slice-extension";
import {
  formatDateDisplay,
  formatTimeDisplay,
} from "../../features/sessions/utils/DisplayMappers";
import AttendancePanel from "../attendance/AttendancePanel";
import OccurrenceActionsMenu from "./OccurrenceActionsMenu";
import OccurrenceOverrideDialog, {
  OverrideMode,
  OverridePayload,
} from "./OccurrenceOverrideDialog";

interface OccurrencesPanelProps {
  session: Session;
  /** Occurrence date (YYYY-MM-DD) whose attendance editor opens by default. */
  initialOpenDate?: string;
}

const STATUS_BADGE: Partial<Record<OccurrenceStatus, { labelKey: string; className: string }>> = {
  [OccurrenceStatus.Cancelled]: {
    labelKey: "sessions.occurrenceList.statusCancelled",
    className: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
  },
  [OccurrenceStatus.Rescheduled]: {
    labelKey: "sessions.occurrenceList.statusRescheduled",
    className: "bg-grey-95 text-grey-30 dark:bg-primary-20 dark:text-grey-70",
  },
  [OccurrenceStatus.Retimed]: {
    labelKey: "sessions.occurrenceList.statusRetimed",
    className: "bg-grey-95 text-grey-30 dark:bg-primary-20 dark:text-grey-70",
  },
};

const isoDate = (d: Date): string => d.toISOString().slice(0, 10);

/**
 * The session's dates: past month + next three months of computed occurrences,
 * with per-date overrides (cancel / reschedule / re-time / restore). Attendance is
 * edited inline, expanded directly under its occurrence row, so it's always clear
 * which date is being marked.
 */
const OccurrencesPanel: React.FC<OccurrencesPanelProps> = ({
  session,
  initialOpenDate,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const occurrences = useAppSelector(selectSessionOccurrences(session.id));
  const loading = useAppSelector(selectOccurrencesLoading);
  // Accounts are single-instructor, so a cancellation's actor is always the owner.
  const instructorName = useAppSelector(selectUserProfile)?.name;

  const [dialog, setDialog] = useState<{ mode: OverrideMode; occurrence: SessionOccurrence } | null>(null);
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The occurrence whose attendance editor is open; the most relevant date opens
  // automatically until the instructor toggles rows themselves.
  const [openDate, setOpenDate] = useState<string | null>(null);
  const userToggled = useRef(false);
  useEffect(() => {
    if (!userToggled.current && initialOpenDate) {
      setOpenDate(initialOpenDate);
    }
  }, [initialOpenDate]);

  const toggleAttendance = (date: string) => {
    userToggled.current = true;
    setOpenDate((current) => (current === date ? null : date));
  };

  const range = useMemo(() => {
    const from = new Date();
    from.setMonth(from.getMonth() - 1);
    const to = new Date();
    to.setMonth(to.getMonth() + 3);
    return { from: isoDate(from), to: isoDate(to) };
  }, []);

  useEffect(() => {
    dispatch(fetchSessionOccurrences({ bookingId: session.id, from: range.from, to: range.to }));
  }, [dispatch, session.id, session.dateModified, range]);

  // Close the row action menu when clicking anywhere outside it.
  useEffect(() => {
    if (menuFor === null) return;
    const close = (e: MouseEvent) => {
      if (!(e.target as Element).closest("[data-occurrence-menu]")) {
        setMenuFor(null);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menuFor]);

  const refresh = () => {
    dispatch(fetchSessionOccurrences({ bookingId: session.id, from: range.from, to: range.to }));
    dispatch(fetchSessionById(session.id));
  };

  const applyOverride = async (mode: OverrideMode, occurrence: SessionOccurrence, payload: OverridePayload) => {
    setBusy(true);
    setError(null);
    const bookingId = session.id;
    const occurrenceDate = occurrence.originalDate;
    try {
      if (mode === "cancel") {
        await dispatch(
          cancelOccurrence({ bookingId, request: { occurrenceDate, reason: payload.reason } })
        ).unwrap();
      } else if (mode === "reschedule") {
        await dispatch(
          rescheduleOccurrence({
            bookingId,
            request: {
              occurrenceDate,
              newDate: payload.newDate!,
              newTime: payload.newTime!,
              reason: payload.reason,
            },
          })
        ).unwrap();
      } else {
        await dispatch(
          retimeOccurrence({
            bookingId,
            request: { occurrenceDate, newTime: payload.newTime!, reason: payload.reason },
          })
        ).unwrap();
      }
      const toastKey: Record<OverrideMode, string> = {
        cancel: "sessions.occurrenceList.toastCancel",
        reschedule: "sessions.occurrenceList.toastReschedule",
        retime: "sessions.occurrenceList.toastRetime",
      };
      setDialog(null);
      toast.success(t(toastKey[mode]));
      refresh();
    } catch (e: unknown) {
      setError((e as { message?: string })?.message ?? t("sessions.occurrenceList.actionFailed"));
    } finally {
      setBusy(false);
    }
  };

  const handleRestore = async (occurrence: SessionOccurrence) => {
    setMenuFor(null);
    try {
      await dispatch(
        restoreOccurrence({
          bookingId: session.id,
          request: { occurrenceDate: occurrence.originalDate },
        })
      ).unwrap();
      toast.success(t("sessions.occurrenceList.toastRestore"));
      refresh();
    } catch (e: unknown) {
      toast.error(
        (e as { message?: string })?.message ?? t("sessions.occurrenceList.actionFailed")
      );
    }
  };

  const statusBadge = (occurrence: SessionOccurrence) => {
    const badge = STATUS_BADGE[occurrence.status];
    if (!badge) return null;
    return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${badge.className}`}>{t(badge.labelKey)}</span>;
  };

  if (!loading && occurrences.length === 0) return null;

  return (
    <div className="with-border rounded-2xl p-4 lg:p-6">
      <div className="flex items-center gap-2">
        <CalendarClock size={20} className="text-grey-30 dark:text-grey-70" />
        <h3>{t("sessions.occurrenceList.title")}</h3>
      </div>

      <div className={`mt-4 space-y-1 overflow-y-auto ${openDate ? "" : "max-h-96"}`}>
        {occurrences.map((occurrence) => {
          const isCancelled = occurrence.status === OccurrenceStatus.Cancelled;
          const hasOverride = occurrence.status !== OccurrenceStatus.Scheduled;
          const isOpen = openDate === occurrence.effectiveDate && !isCancelled;
          return (
            <div key={occurrence.originalDate}>
              <div
                className={`flex items-center justify-between gap-2 rounded-xl px-3 py-2 ${
                  isOpen
                    ? "bg-grey-95 dark:bg-[#3E334D]"
                    : "hover:bg-grey-95 dark:hover:bg-primary-20"
                }`}
              >
                <div className="min-w-0">
                  <p
                    className={`text-sm font-medium ${
                      isCancelled
                        ? "text-grey-40 line-through dark:text-grey-60"
                        : "text-grey-10 dark:text-grey-90"
                    }`}
                  >
                    {formatDateDisplay(occurrence.effectiveDate)} ·{" "}
                    {formatTimeDisplay(occurrence.effectiveTime)}
                  </p>
                  <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-grey-30 dark:text-grey-70">
                    {statusBadge(occurrence)}
                    {occurrence.visitCount > 0 && (
                      <span>
                        {t("sessions.occurrenceList.visitsMarked", { count: occurrence.visitCount })}
                      </span>
                    )}
                    {occurrence.reason && <span>{occurrence.reason}</span>}
                    {isCancelled && occurrence.cancelledAt && (
                      <span>
                        {instructorName
                          ? t("sessions.occurrenceList.cancelledAuditWithName", {
                              date: formatDateDisplay(occurrence.cancelledAt),
                              name: instructorName,
                            })
                          : t("sessions.occurrenceList.cancelledAudit", {
                              date: formatDateDisplay(occurrence.cancelledAt),
                            })}
                      </span>
                    )}
                  </div>
                </div>

                <div className="relative flex shrink-0 items-center gap-1" data-occurrence-menu>
                  {!isCancelled && (
                    <button
                      type="button"
                      onClick={() => toggleAttendance(occurrence.effectiveDate)}
                      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-primary-40 hover:bg-grey-90 dark:text-primary-90 dark:hover:bg-primary-20"
                    >
                      <span>{t("sessions.occurrenceList.markAttendance")}</span>
                      {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() =>
                      setMenuFor(menuFor === occurrence.originalDate ? null : occurrence.originalDate)
                    }
                    className="rounded-md p-1 text-grey-30 hover:bg-grey-90 dark:text-grey-70 dark:hover:bg-primary-20"
                    title={t("sessions.occurrenceList.actions")}
                  >
                    <MoreHorizontal size={16} />
                  </button>

                  {menuFor === occurrence.originalDate && (
                    <OccurrenceActionsMenu
                      isCancelled={isCancelled}
                      hasOverride={hasOverride}
                      onOverride={(mode) => {
                        setMenuFor(null);
                        setError(null);
                        setDialog({ mode, occurrence });
                      }}
                      onRestore={() => handleRestore(occurrence)}
                    />
                  )}
                </div>
              </div>

              {/* Attendance is edited right under its date — no ambiguity which
                  occurrence is being marked. */}
              {isOpen && (
                <div className="mt-1 mb-2 ml-3 border-l-2 border-grey-90 pl-3 dark:border-primary-20">
                  <AttendancePanel
                    session={session}
                    occurrenceDate={occurrence.effectiveDate}
                    embedded
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {dialog && (
        <OccurrenceOverrideDialog
          mode={dialog.mode}
          occurrence={dialog.occurrence}
          busy={busy}
          error={error}
          onConfirm={(payload) => applyOverride(dialog.mode, dialog.occurrence, payload)}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
};

export default OccurrencesPanel;
